import { Observable } from 'rxjs/internal/Observable';
import * as socketIO from "socket.io-client";
import { asArray } from '../../helpers';
import { BusPositionSource, IBusPosition } from '../../interfaces/stadtbus/bus_position';
import { lazyInject, provideSingleton } from '../../inversify';
import Configuration from './../configuration';

@provideSingleton(BusPositionService)
class BusPositionService {
  @lazyInject(Configuration)
  public configuration: Configuration;

  public onBusPositions(preferredSource?: BusPositionSource, maxAge: number = 30): Observable<IBusPosition[]> {
    return this.onBusPosition().pipe(
      this.busPositionsAsArray(preferredSource, maxAge),
    );
  }

  public onBusPosition(): Observable<IBusPosition> {
    return new Observable<IBusPosition>(observer => {
      const socket = socketIO(this.configuration.socketIoEndpoint(), {
        path: this.configuration.socketIoPath(),
        query: {
          'type': 'GPS',
        },
      });

      socket.on('message', (data: any) => {
        // Convert timestamp that might be received as string instead of as Date
        if (typeof data.timestamp === 'string') {
          data.timestamp = new Date(data.timestamp);
        }

        observer.next(data as IBusPosition);
      });

      return () => socket.close();
    });
  }

  public busPositionsAsArray(preferredSource?: BusPositionSource, maxAge: number = 30) {
    return (source: Observable<IBusPosition>) =>
      source.pipe(
        asArray(
          (a: IBusPosition, b: IBusPosition) => a.id === b.id,
          (e: IBusPosition) => {
            let keep = true;

            // Remove if older than 10 minutes.
            const date = new Date();
            date.setMinutes(date.getMinutes() - 10);
            keep = keep && e.timestamp >= date;

            // Remove if no satellites are available
            if (e.metadata !== undefined && e.metadata.satellites !== undefined) {
              keep = keep && e.metadata.satellites > 0;
            }

            return keep;
          },
          (oldDuplicate: IBusPosition | undefined, newPosition: IBusPosition) => {
            if (oldDuplicate === undefined
              || preferredSource === undefined
              || newPosition.source === preferredSource) {
              return true;
            }

            return (new Date().getTime() - oldDuplicate.timestamp.getTime()) > maxAge * 1000;
          }
        )
      )
  }
}

export default BusPositionService;



// WEBPACK FOOTER //
// ./src/api/stadtbus/bus_position_service.ts